/**
 * Footer component.
 * @module components/theme/Footer/Footer
 */

import React from 'react';
import { Container, List, Segment, Image, Grid, GridColumn } from 'semantic-ui-react';
import { isEmpty, map } from 'lodash';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL, addAppURL } from '@plone/volto/helpers/Url/Url';
import { useGoogleAnalytics } from '../../helpers/useGoogleAnalytics';
import {
  usePanelConfigAndPreferences,
  GDPRCookies,
  getLocaleConf, 
} from 'volto-gdpr-privacy/helpers';

const messages = defineMessages({
  copyright: {
    id: 'Copyright',
    defaultMessage: 'Copyright',
  },
  navTitle: {
    id: 'More info',
    defaultMessage: 'More info',
  },
});

// IMAGES
import logoProursa from './img/logo-proursa-blanc.svg';
import logoRubi from './img/ajuntament-rubi-blanc.svg';

/**
 * Component to display the footer.
 * @function Footer
 * @param {Object} intl Intl object
 * @returns {string} Markup of the component
 */
const Footer = ({ intl }) => {
  const { siteActions = [] } = useSelector(
    (state) => ({
      siteActions: state.actions?.actions?.site_actions,
    }),
    shallowEqual,
  );
  const { footerActions = [] } = useSelector(
    (state) => ({
      footerActions: state.actions?.actions?.footer_actions,
    }),
    shallowEqual,
  );

  const token = useSelector((state) => state.userSession.token, shallowEqual);
  const cookies = new GDPRCookies();
  const { defaultPreferences } = usePanelConfigAndPreferences(cookies);
  const gdprPreferences = useSelector(
    (state) => state.gdprPrivacyConsent.preferences ?? defaultPreferences,
  );
  useGoogleAnalytics(gdprPreferences?.prof_GANALYTICS, token)

  return (
    <Segment
      className="portalFooter bg-dark-primary px-3 px-xl-4"
      as="footer"
      vertical
      padded
      inverted 
      id="footer"
      tabIndex="-1"
    >
      
      <Grid divided='vertically'>

        <Grid.Row columns={12}>
          <Grid.Column mobile={12} tablet={12} computer={3} className='mb-4'>
            <Image className='logoFooter mx-auto ms-lg-0 mt-5 mt-lg-3' src={logoProursa} alt="PROURSA. Promocions Urbanes de Rubí s.a." width="212" height="44" />
          </Grid.Column>
          <Grid.Column as='address' mobile={12} tablet={12} computer={3} className='text-center fst-normal text-lg-start mb-4 mt-lg-3'>
            <h2 className='fsc-h5 mt-0 mb-2'><FormattedMessage id="Address" defaultMessage='Address' /></h2>
            <p className='mb-2'>Carrer del General Prim, 33-35,<br /> 4a planta, <br /> 08191 Rubí, Barcelona</p>
            <p className='mb-2'><a href="tel:+34935886693">935 88 66 93</a></p>
            <p className='mb-2'><a href="mailto:proursa@proursa.cat">proursa@proursa.cat</a></p>
          </Grid.Column>
          <Grid.Column mobile={12} tablet={12} computer={3} className='text-center text-lg-start mb-3 mt-lg-3'>
            <h2 className='fsc-h5 mt-0 mb-2'><FormattedMessage id="Schedule" defaultMessage='Schedule' /></h2>
            <p className='mb-2'><FormattedMessage id="Matins" defaultMessage="Matins" />:<br /><FormattedMessage id="De dilluns a divendres, de 9.00 h a 14.00 h" defaultMessage="De dilluns a divendres, de 9.00 h a 14.00 h" /></p>
            <p className='mb-2'><FormattedMessage id="Tardes" defaultMessage="Tardes" />:<br /><FormattedMessage id="Dilluns, de 16.00 h a 18.30 h (Excepte de juny a setembre, període en què l'oficina romandrà tancada a les tardes)" defaultMessage="Dilluns, de 16.00 h a 18.30 h (Excepte de juny a setembre, període en què l'oficina romandrà tancada a les tardes)" /></p>
          </Grid.Column>
          <Grid.Column mobile={12} tablet={12} computer={3} as="nav" aria-label="Footer links">
              {!isEmpty(footerActions) &&
                <ul className='list-unstyled mb-0 d-flex flex-wrap justify-content-center align-items-center flex-lg-column align-items-lg-start'>
                  {map(footerActions, (item) => (
                    <li  key={item.id}>
                      <UniversalLink className="d-block px-3 py-2"
                        href={
                          item.url ? flattenToAppURL(item.url) : addAppURL(item.id)
                        }
                      >
                        {item?.title}
                      </UniversalLink>
                    </li>
                    )
                  )}
                </ul>
              }                
          </Grid.Column>
        </Grid.Row>

        <Grid.Row columns={12} verticalAlign="middle">
          <Grid.Column mobile={12} tablet={12} computer={2} >
            <Image src={logoRubi} alt="Ajuntament de Rubí." className='mx-auto ms-lg-0' width="132" height="68" />              
          </Grid.Column>
          <Grid.Column mobile={12} tablet={12} computer={10} as="nav" aria-label={intl.formatMessage(messages.navTitle)} >
            {!isEmpty(siteActions) &&
              <ul className="list-unstyled mb-0 mt-lg-0 d-flex flex-wrap justify-content-center align-items-center justify-content-lg-end">

                {map(siteActions, (item, index) => (
                  <li  key={item.id}>
                    <UniversalLink className={"d-block px-3 py-2" + (index === siteActions.length - 1 ? " pe-lg-0" : "")}
                      href={
                        item.url ? flattenToAppURL(item.url) : addAppURL(item.id)
                      }
                    >
                      {item?.title}
                    </UniversalLink>
                  </li>
                  )
                )}
              </ul>
            }
          </Grid.Column>
        </Grid.Row>

      </Grid>       
 
    </Segment>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
Footer.propTypes = {
  /**
   * i18n object
   */
};

export default injectIntl(Footer);
